import { useEffect, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/features/auth/AuthProvider";
import { CommandPalette } from "@/features/search/CommandPalette";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/features/toast/ToastProvider";

interface ProtectedShellProps {
  title?: string;
  children: ReactNode;
  // Optional role gate. Single role or array of allowed roles.
  requireRole?: "admin" | "rep" | "finance" | Array<"admin" | "rep" | "finance">;
}

export function ProtectedShell({ children, requireRole }: ProtectedShellProps) {
  const { user, viewedAs, effectiveUser, impersonating, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const qc = useQueryClient();
  const toast = useToast();

  const stopImpersonation = useMutation({
    mutationFn: () => api.post("/api/admin/impersonate", { stop: true }),
    onSuccess: () => {
      toast.ok("Stopped impersonating.");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't stop."),
  });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/login", search: { next: location.pathname } });
      return;
    }
    if (requireRole) {
      const allowed = Array.isArray(requireRole) ? requireRole : [requireRole];
      // Gate on the effective role: while impersonating a rep, an admin is
      // treated as that rep and is routed out of admin/finance-only pages (the
      // "Stop impersonating" banner is the way back).
      const effectiveRole = (effectiveUser ?? user).role;
      if (!allowed.includes(effectiveRole)) {
        navigate({ to: "/" }); // role-redirect index will route to the right place
      }
    }
  }, [user, effectiveUser, loading, requireRole, navigate, location.pathname]);

  if (loading || !user) {
    return (
      <div className="container">
        <p className="lede" style={{ textAlign: "center", marginTop: 80 }}>
          Loading…
        </p>
      </div>
    );
  }

  return (
    <>
      {impersonating && viewedAs && (
        <div
          style={{
            background: "var(--accent-deep)",
            color: "#fff",
            padding: "9px 24px",
            fontSize: 12.5,
            fontWeight: 500,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            borderBottom: "1px solid var(--primary800)",
          }}
        >
          <span>
            Impersonating <strong>{viewedAs.full_name ?? viewedAs.email}</strong>.
          </span>
          <button
            type="button"
            onClick={() => stopImpersonation.mutate()}
            disabled={stopImpersonation.isPending}
            style={{
              background: "rgba(255, 255, 255, 0.14)",
              color: "#fff",
              border: "1px solid rgba(255, 255, 255, 0.22)",
              borderRadius: 999,
              padding: "3px 12px",
              fontSize: 11.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {stopImpersonation.isPending ? "Stopping…" : "Stop impersonating"}
          </button>
        </div>
      )}
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "14px 40px",
          borderBottom: "1px solid var(--hairline)",
          background: "color-mix(in srgb, var(--bg) 80%, transparent)",
          backdropFilter: "saturate(160%) blur(14px)",
        }}
      >
        <strong style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}>
          Bookboost Commissions
        </strong>
        <div style={{ display: "flex", gap: 16, alignItems: "center", fontSize: 12, color: "var(--muted)" }}>
          <span className="muted" style={{ fontSize: 11 }} title="⌘K to search">
            ⌘K
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{user.email}</span>
          <button
            onClick={() => signOut().then(() => navigate({ to: "/login" }))}
            style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer" }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="container">{children}</main>
      <CommandPalette />
    </>
  );
}
